import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  buyerSubmissionsApi,
  listingsApi,
  type SendSellerPrequalInvitePayload,
} from "@/api/buyers";
import { buyerSubmissionKeys, listingKeys } from "./buyerKeys";

/**
 * The SELLER's prequalification — the same row, reachable from two addresses.
 *
 * 🔴 **Every mutation invalidates `listingKeys.all` as well as
 * `buyerSubmissionKeys.all`, whichever address wrote it, because the write lands
 * on the LISTING.** The submission prefix covers the summary and the escalation
 * gate — whose warning reads the same signal, and which would otherwise keep
 * saying "no seller prequalification" for the five minutes of `staleTime` after
 * one was linked, with no recovery. The listing prefix covers the row that
 * actually changed: every other submission against the same listing reads
 * through it, and this is the one link in the module that is deliberately not
 * scoped to the record the specialist is looking at.
 *
 * 🔴 **THE TWO ADDRESSES ARE NOT INTERCHANGEABLE, and that is why both exist.**
 * The listing address is the primary one — the record describes the seller and
 * the business, so the listing is where the surface that manages it lives. The
 * submission address stays because a specialist standing on a submission is
 * genuinely where an invitation came from, the invitation records it, and —
 * load-bearing — the two carry DIFFERENT authorization gates. A
 * submission-addressed write takes `BuyerOwnership.RequireWrite` against the
 * buyer behind it; a listing-addressed write takes `Listings.Manage` and no row
 * check, which is `ListingsController`'s own documented rule. Re-pointing a
 * submission-side caller at the listing hooks would quietly let a specialist who
 * does not own the buyer send a seller invitation, as a side effect of a UI
 * change rather than as a decision anyone made.
 */

/** One invalidation for both addresses. Neither prefix is optional: see above. */
function useInvalidateSellerPrequalification() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: buyerSubmissionKeys.all });
    void qc.invalidateQueries({ queryKey: listingKeys.all });
  };
}

// ---------------------------------------------------------------------------
// Addressed by SUBMISSION — the provenance address.
// ---------------------------------------------------------------------------

/** The listing's linked seller prequalification, addressed by submission. */
export function useSellerPrequalification(submissionId: string, enabled = true) {
  return useQuery({
    queryKey: buyerSubmissionKeys.sellerPrequalification(submissionId),
    queryFn: () => buyerSubmissionsApi.getSellerPrequalification(submissionId),
    enabled: enabled && Boolean(submissionId),
  });
}

/** Seller prequalifications matching a typed email. Disabled while the box is
 *  empty: there is no seller contact record to default from, so an unasked
 *  search must not be answered with the whole book. */
export function useSellerPrequalificationCandidates(
  submissionId: string, search: string, enabled = true,
) {
  return useQuery({
    queryKey: buyerSubmissionKeys.sellerPrequalificationCandidates(submissionId, search),
    queryFn: () =>
      buyerSubmissionsApi.findSellerPrequalificationCandidates(submissionId, search),
    enabled: enabled && Boolean(submissionId) && search.trim().length > 0,
  });
}

/** Seller invitations sent from this submission, newest first. */
export function useSellerPrequalificationInvites(submissionId: string, enabled = true) {
  return useQuery({
    queryKey: buyerSubmissionKeys.sellerPrequalificationInvites(submissionId),
    queryFn: () => buyerSubmissionsApi.listSellerPrequalificationInvites(submissionId),
    enabled: enabled && Boolean(submissionId),
  });
}

/**
 * Invite the seller.
 *
 * A 409 carries the duplicate warning; the surface re-submits with
 * `confirmDuplicate: true` after the sender confirms. An override, never a
 * suppression — the first attempt always gets the warning.
 */
export function useSendSellerPrequalInvite(submissionId: string) {
  const invalidate = useInvalidateSellerPrequalification();
  return useMutation({
    mutationFn: (payload: SendSellerPrequalInvitePayload) =>
      buyerSubmissionsApi.sendSellerPrequalificationInvite(submissionId, payload),
    onSuccess: invalidate,
  });
}

/**
 * Resend one invitation to its RECIPIENT — the manual nudge.
 *
 * One invite, one click. There is deliberately no bulk variant here either: the
 * daily send cap is enforced only on create, so a list-level action over this
 * path is precisely what the ceiling exists to stop.
 */
export function useResendSellerPrequalInvite(submissionId: string) {
  const invalidate = useInvalidateSellerPrequalification();
  return useMutation({
    mutationFn: (inviteId: string) =>
      buyerSubmissionsApi.resendSellerPrequalificationInvite(submissionId, inviteId),
    onSuccess: invalidate,
  });
}

/** Link a confirmed candidate to the LISTING. */
export function useLinkSellerPrequalification(submissionId: string) {
  const invalidate = useInvalidateSellerPrequalification();
  return useMutation({
    mutationFn: (prequalSubmissionId: string) =>
      buyerSubmissionsApi.linkSellerPrequalification(submissionId, prequalSubmissionId),
    onSuccess: invalidate,
  });
}

/** Clear the listing's pointer. Idempotent server-side. */
export function useUnlinkSellerPrequalification(submissionId: string) {
  const invalidate = useInvalidateSellerPrequalification();
  return useMutation({
    mutationFn: () => buyerSubmissionsApi.unlinkSellerPrequalification(submissionId),
    onSuccess: invalidate,
  });
}

// ---------------------------------------------------------------------------
// Addressed by LISTING — the record the fact belongs to.
// ---------------------------------------------------------------------------

/** This listing's seller prequalification. */
export function useListingSellerPrequalification(listingId: string, enabled = true) {
  return useQuery({
    queryKey: listingKeys.sellerPrequalification(listingId),
    queryFn: () => listingsApi.getSellerPrequalification(listingId),
    enabled: enabled && Boolean(listingId),
  });
}

/** @see useSellerPrequalificationCandidates — same rule about an empty box. */
export function useListingSellerPrequalificationCandidates(
  listingId: string, search: string, enabled = true,
) {
  return useQuery({
    queryKey: listingKeys.sellerPrequalificationCandidates(listingId, search),
    queryFn: () => listingsApi.findSellerPrequalificationCandidates(listingId, search),
    enabled: enabled && Boolean(listingId) && search.trim().length > 0,
  });
}

/** 🔴 The UNION — invitations sent from this listing AND from every submission
 *  against it. A listing-only list would answer "has this seller already been
 *  approached?" with a confident no for a seller invited from a submission last
 *  week, and that answer is what gets a second link mailed. */
export function useListingSellerPrequalificationInvites(listingId: string, enabled = true) {
  return useQuery({
    queryKey: listingKeys.sellerPrequalificationInvites(listingId),
    queryFn: () => listingsApi.listSellerPrequalificationInvites(listingId),
    enabled: enabled && Boolean(listingId),
  });
}

export function useSendListingSellerPrequalInvite(listingId: string) {
  const invalidate = useInvalidateSellerPrequalification();
  return useMutation({
    mutationFn: (payload: SendSellerPrequalInvitePayload) =>
      listingsApi.sendSellerPrequalificationInvite(listingId, payload),
    onSuccess: invalidate,
  });
}

export function useResendListingSellerPrequalInvite(listingId: string) {
  const invalidate = useInvalidateSellerPrequalification();
  return useMutation({
    mutationFn: (inviteId: string) =>
      listingsApi.resendSellerPrequalificationInvite(listingId, inviteId),
    onSuccess: invalidate,
  });
}

export function useLinkListingSellerPrequalification(listingId: string) {
  const invalidate = useInvalidateSellerPrequalification();
  return useMutation({
    mutationFn: (prequalSubmissionId: string) =>
      listingsApi.linkSellerPrequalification(listingId, prequalSubmissionId),
    onSuccess: invalidate,
  });
}

export function useUnlinkListingSellerPrequalification(listingId: string) {
  const invalidate = useInvalidateSellerPrequalification();
  return useMutation({
    mutationFn: () => listingsApi.unlinkSellerPrequalification(listingId),
    onSuccess: invalidate,
  });
}
